<template>
  <b-card>
    <b-row>
      <b-col>
        <h1 class="mb-2">Change Password</h1>
      </b-col>
    </b-row>

    <b-alert
      v-if="mustChangePassword"
      show
      variant="warning"
      class="mb-2"
    >
      Please change your password to continue.
    </b-alert>

    <b-form class="d-flex flex-column" @submit="onPasswordSubmit">
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Current Password" label-for="current-password">
            <b-input-group class="input-group-merge">
              <input
                id="current-password"
                v-model="pwd.currentPassword"
                :type="pwd.showCurrentPassword ? 'text' : 'password'"
                class="form-control form-control-merge"
                required
              >
              <b-input-group-append is-text>
                <feather-icon
                  class="cursor-pointer"
                  :icon="pwd.showCurrentPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  @click="toggleCurrentPasswordVisibility"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-form-group label="New Password" label-for="new-password">
            <b-input-group class="input-group-merge">
              <input
                id="new-password"
                v-model="pwd.newPassword"
                :type="pwd.showNewPassword ? 'text' : 'password'"
                class="form-control form-control-merge"
                required
              >
              <b-input-group-append is-text>
                <feather-icon
                  class="cursor-pointer"
                  :icon="pwd.showNewPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  @click="toggleNewPasswordVisibility"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-form-group label="Repeat New Password" label-for="password-confirmation">
            <b-input-group class="input-group-merge">
              <input
                id="password-confirmation"
                v-model="pwd.confirmPassword"
                :type="pwd.showConfirmPassword ? 'text' : 'password'"
                class="form-control form-control-merge"
                required
              >
              <b-input-group-append is-text>
                <feather-icon
                  class="cursor-pointer"
                  :icon="pwd.showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  @click="toggleConfirmPasswordVisibility"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <small class="text-muted mb-1">
        Password must be at least 8 characters and contain a number, one uppercase letter, and one special character.
      </small>

      <b-button type="submit" variant="primary" class="align-self-end mt-1" :disabled="isPasswordSaving">
        <b-spinner v-if="isPasswordSaving" label="Saving password" small />
        <span v-else>Change Password</span>
      </b-button>
    </b-form>
  </b-card>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import { isStaffMember, staffMustChangePassword } from '@/libs/staffPermissions'

const store = useStore()
const router = useRouter()
const isPasswordSaving = ref(false)
const user = computed(() => store.getters['auth/user'])
const mustChangePassword = computed(() => staffMustChangePassword(user.value))

const pwd = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  showCurrentPassword: false,
  showNewPassword: false,
  showConfirmPassword: false,
})

const toggleCurrentPasswordVisibility = () => {
  pwd.showCurrentPassword = !pwd.showCurrentPassword
}

const toggleNewPasswordVisibility = () => {
  pwd.showNewPassword = !pwd.showNewPassword
}

const toggleConfirmPasswordVisibility = () => {
  pwd.showConfirmPassword = !pwd.showConfirmPassword
}

const onPasswordSubmit = async (event) => {
  if (event && event.preventDefault) {
    event.preventDefault()
  }
  if (!pwd.currentPassword || !pwd.newPassword || !pwd.confirmPassword) {
    toastFailure('Please fill all password fields.')
    return
  }
  if (pwd.newPassword !== pwd.confirmPassword) {
    toastFailure('Passwords do not match.')
    return
  }
  if (pwd.newPassword === pwd.currentPassword) {
    toastFailure('New password must be different from your current password.')
    return
  }

  const passwordRegex = /^(?=.*[A-Za-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/
  if (!passwordRegex.test(pwd.newPassword)) {
    toastFailure('Password must be at least 8 characters and include number, uppercase letter and special character.')
    return
  }

  const wasForcedChange = mustChangePassword.value
  isPasswordSaving.value = true
  try {
    await store.dispatch('auth/changePassword', {
      current_password: pwd.currentPassword,
      password: pwd.newPassword,
      password_confirmation: pwd.confirmPassword,
    })

    if (wasForcedChange || isStaffMember(user.value)) {
      toastSuccess('Password successfully changed')
      await router.replace({ name: 'admin' })
      return
    }

    toastSuccess('Password changed. Please log in again.')
    await store.dispatch('auth/logout')
    router.replace('/login')
  } catch (error) {
    toastFailure(backendErrorString(error))
  } finally {
    isPasswordSaving.value = false
    pwd.currentPassword = ''
    pwd.newPassword = ''
    pwd.confirmPassword = ''
    pwd.showCurrentPassword = false
    pwd.showNewPassword = false
    pwd.showConfirmPassword = false
  }
}
</script>
